'use client'

import React from 'react'
import Img from '../../Img'
import styles from './styles.module.css'
import {
  BUDGET_BANDS,
  SCREEN_LAYOUTS,
  SCREEN_OPTIONS,
  MOUNT_SCREEN_PROFILE,
  SHIPPING_OPTIONS,
  ASSEMBLY_OPTIONS,
  SUBSCRIPTION_BLURB,
} from './catalog'

// The guided ("new to sim racing") question flow.
//
// It owns NO build state of its own. Every answer is written straight into the
// same `chosen` / `multi` / `quantities` state the expert configurator uses, via
// the `onAnswer` callback — which is what keeps the "Your build" panel live
// beside the questions and lets the rider drop into the full configurator at any
// point without losing anything.

export const QUESTIONS = [
  'name',
  'age',
  'budget',
  'colour',
  'seat',
  'layout',
  'size',
  'wheels',
  'shifter',
  'keyboardTray',
  'pcTray',
  'clutch',
  'screenWheel',
  'shipping',
  'assembly',
  'academy',
  'done',
]

export const MIN_AGE = 8

function QuestionShell({ index, total, title, caption, children, onBack }) {
  return (
    <section className={styles.step}>
      <div className={styles.qProgress}>
        <span className={styles.qProgressBar}>
          <span className={styles.qProgressFill} style={{ width: `${Math.round((index / total) * 100)}%` }} />
        </span>
        <span className={styles.qProgressText}>Question {index + 1} of {total}</span>
      </div>
      <h2 className={styles.qTitle}>{title}</h2>
      {caption && <p className={styles.stepCaption}>{caption}</p>}
      <div className={styles.qBody}>{children}</div>
      {onBack && (
        <div className={styles.qNav}>
          <button type="button" className={styles.linkBtn} onClick={onBack}>Back</button>
        </div>
      )}
    </section>
  )
}

// A plain yes/no question that toggles one piece of hardware in or out.
function YesNo({ value, onYes, onNo, yesLabel, yesMeta, noLabel, noMeta }) {
  return (
    <div className={styles.grid}>
      <button type="button" className={[styles.card, styles.cardPlain, value === true && styles.cardSelected].filter(Boolean).join(' ')} onClick={onYes} aria-pressed={value === true}>
        <span className={styles.cardBody}>
          <span className={styles.cardName}>{yesLabel}</span>
          {yesMeta && <span className={styles.cardMeta}>{yesMeta}</span>}
        </span>
        <span className={styles.tick} aria-hidden="true" />
      </button>
      <button type="button" className={[styles.card, styles.cardPlain, value === false && styles.cardSelected].filter(Boolean).join(' ')} onClick={onNo} aria-pressed={value === false}>
        <span className={styles.cardBody}>
          <span className={styles.cardName}>{noLabel}</span>
          {noMeta && <span className={styles.cardMeta}>{noMeta}</span>}
        </span>
        <span className={styles.tick} aria-hidden="true" />
      </button>
    </div>
  )
}

function Guided({
  question,
  answers,
  rigOptions,
  rigImageFor,
  offerFor,
  offerName,
  offerPrice,
  mountAvailable,
  bandLabel,
  formatEur,
  plans,
  planCode,
  submitState,
  onAnswer,
  onBack,
  onSubmitOrder,
}) {
  const total = QUESTIONS.length - 1
  const index = Math.max(0, QUESTIONS.indexOf(question))
  const back = index > 0 ? onBack : null
  const shell = (title, caption, children) => (
    <QuestionShell index={index} total={total} title={title} caption={caption} onBack={back}>{children}</QuestionShell>
  )

  if (question === 'name') {
    return shell('Who is this rig for?', 'First name is plenty. It just makes the rest of this less robotic.', (
      <form
        className={styles.qForm}
        onSubmit={(e) => {
          e.preventDefault()
          const v = new FormData(e.currentTarget).get('name')
          onAnswer('name', String(v || '').trim() || 'there')
        }}
      >
        <input className={styles.qInput} name="name" type="text" autoComplete="given-name" placeholder="Their first name" defaultValue={answers.name || ''} aria-label="First name" />
        <button type="submit" className={styles.qSubmit}>Continue</button>
      </form>
    ))
  }

  if (question === 'age') {
    return shell(
      `Hi ${answers.name || 'there'}, how old are you?`,
      `This is not idle curiosity. Under 16 we cap the wheel base torque, because a 20 Nm direct-drive base is genuinely strong. ${MIN_AGE} and up.`,
      (
        <form
          className={styles.qForm}
          onSubmit={(e) => {
            e.preventDefault()
            const v = Number(new FormData(e.currentTarget).get('age'))
            if (!Number.isFinite(v) || v < MIN_AGE) return
            onAnswer('age', v)
          }}
        >
          <input className={styles.qInput} name="age" type="number" min={MIN_AGE} max="99" placeholder={`Age (${MIN_AGE}+)`} defaultValue={answers.age || ''} aria-label="Age" required />
          <button type="submit" className={styles.qSubmit}>Continue</button>
        </form>
      )
    )
  }

  if (question === 'budget') {
    return shell('What are you looking to spend?', 'Everything after this is picked to land as close to this figure as we can without going over.', (
      <div className={styles.grid}>
        {BUDGET_BANDS.map((b) => (
          <button
            type="button"
            key={b.id}
            className={[styles.card, styles.cardPlain, answers.budget === b.id && styles.cardSelected].filter(Boolean).join(' ')}
            onClick={() => onAnswer('budget', b.id)}
            aria-pressed={answers.budget === b.id}
          >
            <span className={styles.cardBody}>
              <span className={styles.cardName}>{bandLabel(b)}</span>
              <span className={styles.cardMeta}>{b.blurb}</span>
            </span>
            <span className={styles.tick} aria-hidden="true" />
          </button>
        ))}
      </div>
    ))
  }

  if (question === 'colour') {
    return shell('Which colour do you like?', 'Same cockpit, same aluminium, seven powder-coat finishes.', (
      <div className={styles.grid}>
        {rigOptions.map((o) => (
          <button
            type="button"
            key={o.type}
            className={[styles.card, answers.rig === o.type && styles.cardSelected].filter(Boolean).join(' ')}
            onClick={() => onAnswer('rig', o.type)}
            aria-pressed={answers.rig === o.type}
          >
            <span className={styles.cardMedia}>
              <Img image={rigImageFor(o)} sizes="220px" layout="fill" objectFit="contain" reserveSpace={false} alt={offerName(o)} />
            </span>
            <span className={styles.cardBody}>
              <span className={styles.cardName}>{(o.colorInfo && o.colorInfo.colorName) || offerName(o)}</span>
            </span>
            <span className={styles.tick} aria-hidden="true" />
          </button>
        ))}
      </div>
    ))
  }

  if (question === 'seat') {
    return shell('Do you want our seat?', 'The GT-2 seat is a fibreglass-shelled fixed-back bucket. If you already own a seat, keep it — the rig takes standard side mounts.', (
      <YesNo
        value={answers.seat}
        onYes={() => onAnswer('seat', true)}
        onNo={() => onAnswer('seat', false)}
        yesLabel="Yes, include the GT-2 seat"
        yesMeta="Fitted and aligned before it ships."
        noLabel="No, I have my own seat"
        noMeta="The photos update to the seatless rig."
      />
    ))
  }

  if (question === 'layout') {
    return shell('How do you want to see the track?', 'This decides the stand, and the stand decides which screens fit.', (
      <div className={styles.grid}>
        {SCREEN_LAYOUTS.filter((l) => l.sizes.some((sz) => mountAvailable(sz.mount))).map((l) => {
          const preview = offerFor(l.previewType)
          return (
            <button
              type="button"
              key={l.id}
              className={[styles.card, answers.layout === l.id && styles.cardSelected].filter(Boolean).join(' ')}
              onClick={() => onAnswer('layout', l.id)}
              aria-pressed={answers.layout === l.id}
            >
              {preview && (
                <span className={styles.cardMedia}>
                  <Img image={(preview.galleryImages && preview.galleryImages[0]) || preview.imageUrl} sizes="220px" layout="fill" objectFit="contain" reserveSpace={false} alt={l.title} />
                </span>
              )}
              <span className={styles.cardBody}>
                <span className={styles.cardName}>{l.title}</span>
                <span className={styles.cardMeta}>{l.blurb}</span>
              </span>
              <span className={styles.tick} aria-hidden="true" />
            </button>
          )
        })}
      </div>
    ))
  }

  if (question === 'size') {
    const layout = SCREEN_LAYOUTS.find((l) => l.id === answers.layout) || SCREEN_LAYOUTS[0]
    // Show the actual SCREENS the chosen stand can carry, not just the inch
    // sizes: the size alone means nothing to a newcomer, and the screen is where
    // most of the money goes. Each row still carries the size and which stand it
    // implies, so the choice sets both in one click.
    const rows = layout.sizes
      .filter((sz) => mountAvailable(sz.mount))
      .flatMap((sz) => {
        const profile = MOUNT_SCREEN_PROFILE[sz.mount]
        return (SCREEN_OPTIONS[profile] || [])
          .filter((m) => m.size === sz.size)
          .map((m) => ({ ...m, mount: sz.mount, freeStanding: sz.freeStanding }))
      })
    return shell(
      'Choose your monitor size',
      layout.id === 'single'
        ? 'One screen. The wider panel costs more but replaces a triple setup.'
        : 'Three of whichever you pick, priced per screen and as a set. Bigger needs more room behind the rig, and more GPU.',
      (
        <div className={styles.grid}>
          {rows.map((m) => {
            const mount = offerFor(m.mount)
            const each = formatEur(m.priceEur)
            const set = formatEur(m.priceEur * m.qty)
            return (
              <button
                type="button"
                key={m.id}
                className={[styles.card, styles.cardPlain, answers.screenId === m.id && styles.cardSelected].filter(Boolean).join(' ')}
                onClick={() => onAnswer('screenPick', { size: m.size, id: m.id })}
                aria-pressed={answers.screenId === m.id}
              >
                <span className={styles.cardBody}>
                  <span className={styles.sizePill}>{m.size}</span>
                  <span className={styles.cardName}>{m.name}</span>
                  <span className={styles.cardMeta}>{m.spec}</span>
                  <span className={styles.cardPrice}>
                    {m.qty > 1 ? `${each} each` : each}
                    {m.qty > 1 && <span className={styles.est}> · {set} for {m.qty}</span>}
                  </span>
                  {/* Which stand this implies, and whether it bolts to the rig or
                      stands on its own — the thing the product name never says. */}
                  <span className={styles.cardNote}>
                    {mount ? offerName(mount) : m.size} · {m.freeStanding ? 'free-standing' : 'mounts on the chassis'}
                    {mount ? ` · ${offerPrice(mount)}` : ''}
                  </span>
                </span>
                <span className={styles.tick} aria-hidden="true" />
              </button>
            )
          })}
        </div>
      )
    )
  }

  if (question === 'wheels') {
    return shell('Will you run more than one steering wheel?', 'Most people end up with a round GT wheel and a formula wheel. Each extra wheel needs its own quick-release adapter, so we add one per wheel.', (
      <div className={styles.grid}>
        {[0, 1, 2, 3, 5].map((n) => (
          <button
            type="button"
            key={n}
            className={[styles.card, styles.cardPlain, answers.extraWheels === n && styles.cardSelected].filter(Boolean).join(' ')}
            onClick={() => onAnswer('extraWheels', n)}
            aria-pressed={answers.extraWheels === n}
          >
            <span className={styles.cardBody}>
              <span className={styles.cardName}>{n === 0 ? 'Just the one' : `${n} extra wheel${n > 1 ? 's' : ''}`}</span>
              <span className={styles.cardMeta}>{n === 0 ? 'No extra adapters.' : `Adds ${n} quick-release adapter${n > 1 ? 's' : ''}.`}</span>
            </span>
            <span className={styles.tick} aria-hidden="true" />
          </button>
        ))}
      </div>
    ))
  }

  if (question === 'shifter') {
    const item = offerFor('SHIFTER_MOUNT')
    return shell('Will you use a shifter or a handbrake?', 'Rally and older GT cars want both. They bolt to a side mount rather than sitting loose on the desk.', (
      <YesNo
        value={answers.shifter}
        onYes={() => onAnswer('shifter', true)}
        onNo={() => onAnswer('shifter', false)}
        yesLabel="Yes, add the mount"
        yesMeta={item ? `${offerName(item)} — ${offerPrice(item)}` : null}
        noLabel="No, not for now"
        noMeta="You can bolt one on later."
      />
    ))
  }

  if (question === 'keyboardTray') {
    const item = offerFor('KEYBOARD_MOUNT')
    return shell('Somewhere to put the keyboard and mouse?', 'A tray that swings out of the way while you drive, so your desk stays out of the cockpit.', (
      <YesNo
        value={answers.keyboardTray}
        onYes={() => onAnswer('keyboardTray', true)}
        onNo={() => onAnswer('keyboardTray', false)}
        yesLabel="Yes, add the tray"
        yesMeta={item ? `${offerName(item)} — ${offerPrice(item)}` : null}
        noLabel="No thanks"
      />
    ))
  }

  if (question === 'pcTray') {
    const item = offerFor('PC_TRAY')
    return shell('Where will the PC live?', 'Keep it off the floor and out of the dust. The tray hangs inside the frame, cables and all.', (
      <YesNo
        value={answers.pcTray}
        onYes={() => onAnswer('pcTray', true)}
        onNo={() => onAnswer('pcTray', false)}
        yesLabel="Yes, add the PC tray"
        yesMeta={item ? `${offerName(item)} — ${offerPrice(item)}` : null}
        noLabel="No, it sits elsewhere"
      />
    ))
  }

  if (question === 'clutch') {
    return shell('Will you drive with a manual shifter?', 'A clutch only earns its place if you are shifting manually. Otherwise two pedals is the faster, simpler setup.', (
      <YesNo
        value={answers.clutch}
        onYes={() => onAnswer('clutch', true)}
        onNo={() => onAnswer('clutch', false)}
        yesLabel="Yes, I want a clutch"
        yesMeta="Upgrades to the 3-Pedal Set."
        noLabel="No, throttle and brake is fine"
        noMeta="The 2-pedal set. You can add a clutch later."
      />
    ))
  }

  if (question === 'screenWheel') {
    const item = offerFor('VRS_ASCHER_ULTIMATE_WHEEL_QR_CONNECT_WHEEL_ADAPTER')
    return shell('Do you want a wheel with a built-in screen?', 'The VRS x ASCHER Ultimate puts your delta, fuel and tyre temps on the wheel itself, so your eyes stay on the track.', (
      <YesNo
        value={answers.screenWheel}
        onYes={() => onAnswer('screenWheel', true)}
        onNo={() => onAnswer('screenWheel', false)}
        yesLabel="Yes, the ASCHER Ultimate"
        yesMeta={item ? `${offerName(item)} — ${offerPrice(item)}` : null}
        noLabel="No, keep the R295"
        noMeta="Still a full RGB rim with paddles and encoders."
      />
    ))
  }

  if (question === 'shipping') {
    return shell('How should we get it to you?', 'A full rig travels as freight, not a parcel.', (
      <div className={styles.grid}>
        {SHIPPING_OPTIONS.map((x) => (
          <button
            type="button"
            key={x.id}
            className={[styles.card, styles.cardPlain, answers.shipping === x.id && styles.cardSelected].filter(Boolean).join(' ')}
            onClick={() => onAnswer('shipping', x.id)}
            aria-pressed={answers.shipping === x.id}
          >
            <span className={styles.cardBody}>
              <span className={styles.cardName}>{x.id === 'ship-pickup' ? 'I will arrange collection' : `You organise it — ${x.name.toLowerCase()}`}</span>
              <span className={styles.cardMeta}>{x.spec}</span>
              <span className={styles.cardPrice}>{formatEur(x.priceEur)}</span>
              {x.placeholder && <span className={styles.cardNote}>Placeholder rate</span>}
            </span>
            <span className={styles.tick} aria-hidden="true" />
          </button>
        ))}
      </div>
    ))
  }

  if (question === 'assembly') {
    return shell('Build it yourself, or have it arrive ready?', 'Either is fine. Self-assembly is a couple of hours with two people and the manuals.', (
      <div className={styles.grid}>
        {ASSEMBLY_OPTIONS.map((x) => (
          <button
            type="button"
            key={x.id}
            className={[styles.card, styles.cardPlain, answers.assembly === x.id && styles.cardSelected].filter(Boolean).join(' ')}
            onClick={() => onAnswer('assembly', x.id)}
            aria-pressed={answers.assembly === x.id}
          >
            <span className={styles.cardBody}>
              <span className={styles.cardName}>{x.name}</span>
              <span className={styles.cardMeta}>{x.spec}</span>
              <span className={styles.cardPrice}>{formatEur(x.priceEur)}</span>
              {x.placeholder && <span className={styles.cardNote}>Placeholder price</span>}
            </span>
            <span className={styles.tick} aria-hidden="true" />
          </button>
        ))}
      </div>
    ))
  }

  if (question === 'academy') {
    return shell('Get faster with VRS Academy', 'The hardware is half of it. Academy is your telemetry, the data packs and the tools to read them. Billed monthly, separately from the rig.', (
      <div className={styles.grid}>
        {plans.map((p) => {
          const monthly = p.price && p.price.monthly ? p.price.monthly.priceCents : 0
          return (
            <button
              type="button"
              key={p.code}
              className={[styles.card, styles.cardPlain, planCode === p.code && styles.cardSelected].filter(Boolean).join(' ')}
              onClick={() => onAnswer('plan', p.code)}
              aria-pressed={planCode === p.code}
            >
              <span className={styles.cardBody}>
                <span className={styles.cardName}>{p.name}</span>
                <span className={styles.cardMeta}>{SUBSCRIPTION_BLURB[p.code] || p.description}</span>
                <span className={styles.cardPrice}>{monthly ? `${formatEur(monthly / 100)} / month` : 'Free'}</span>
              </span>
              <span className={styles.tick} aria-hidden="true" />
            </button>
          )
        })}
      </div>
    ))
  }

  // done — the order step
  return (
    <section className={styles.step}>
      <h2 className={styles.qTitle}>{answers.name ? `${answers.name}, here is your rig` : 'Here is your rig'}</h2>
      <p className={styles.stepCaption}>
        We picked the strongest parts your budget covers, in the order that matters on track: wheel base first, then
        pedals, then screens. The full list is beside you. Send it over and we will come back to confirm the build,
        the real shipping rate and a delivery date.
      </p>
      {submitState.status === 'sent' ? (
        <div className={styles.sentBox}>
          <strong>Order sent.</strong>
          <span>We have your build and will be in touch at {submitState.email}.</span>
        </div>
      ) : (
        <form
          className={styles.orderForm}
          onSubmit={(e) => {
            e.preventDefault()
            const fd = new FormData(e.currentTarget)
            onSubmitOrder({
              email: String(fd.get('email') || '').trim(),
              phone: String(fd.get('phone') || '').trim(),
            })
          }}
        >
          <div className={styles.qForm}>
            <input className={styles.qInput} name="email" type="email" required placeholder="Your email" aria-label="Your email" autoComplete="email" />
            <input className={styles.qInput} name="phone" type="tel" placeholder="Phone (optional)" aria-label="Phone" autoComplete="tel" />
          </div>
          {submitState.status === 'error' && <p className={styles.orderError}>{submitState.error}</p>}
          <button type="submit" className={styles.buyBtn} disabled={submitState.status === 'sending'}>
            {submitState.status === 'sending' ? 'Sending…' : 'Buy — finalise order'}
          </button>
        </form>
      )}
      <div className={styles.qNav}>
        <button type="button" className={styles.linkBtn} onClick={onBack}>Back</button>
      </div>
    </section>
  )
}

export default Guided
